/* CARILOHA: Intelligems test CAR_2026Q4_14 — "Upgrade to the bundle" swap in the cart drawer.
   Card markup + the bundle's cart lines come from snippets/cariloha-bundle-swap.liquid. The
   lines mirror bundle-configurator.js#addBundleToCart (same _bundle_* properties), so the bundle
   discount and the cart's bundle grouping treat them like a bundle added from the bundle page.

   Order: add the bundle lines first, then remove the original sheet line, so a failed add never
   leaves the shopper with an emptier cart. Follows the CartLinesUpdateEvent pattern used in
   bundle-configurator.js / component-cart-items.js (dispatch with a deferred promise, resolve
   with the final cart + sections so the drawer morphs). */

import { CartLinesUpdateEvent } from '@shopify/events';

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-cariloha-bundle-swap]') : null;
  if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

  swapToBundle(button);
});

/**
 * @param {HTMLButtonElement} button
 */
async function swapToBundle(button) {
  const card = button.closest('.cariloha-bundle-swap');
  const dataEl = card?.querySelector('[data-cariloha-bundle-items]');
  const errorEl = card?.querySelector('.cariloha-bundle-swap__error');
  const lineKey = button.dataset.lineKey;
  if (!dataEl || !lineKey) return;

  /** @type {{id: number, quantity: number, properties: Record<string, string>}[]} */
  const bundleItems = JSON.parse(dataEl.textContent || '[]');
  if (bundleItems.length === 0) return;

  const bundleId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const items = bundleItems.map((item) => ({
    ...item,
    properties: { _bundle_id: bundleId, ...item.properties },
  }));

  const sectionIds = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  }

  button.setAttribute('aria-busy', 'true');
  if (errorEl instanceof HTMLElement) errorEl.hidden = true;

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const addResponse = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const addData = await addResponse.json();

    if (addData.status) {
      const currentCart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart),
        detail: { didError: true, source: 'cariloha-bundle-swap', itemCount: 0 },
      });
      showError(errorEl, addData.description || addData.message || 'Could not upgrade to the bundle.');
      button.removeAttribute('aria-busy');
      return;
    }

    // /cart/change.js returns the full cart (with currency) plus the requested sections.
    const changeResponse = await fetch(`${Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: Array.from(sectionIds).join(','),
        sections_url: window.location.pathname,
      }),
    });
    const updatedCart = await changeResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
      detail: {
        sections: updatedCart.sections,
        items: updatedCart.items,
        itemCount: updatedCart.item_count,
        source: 'cariloha-bundle-swap',
        didError: false,
      },
    });
  } catch (error) {
    console.error('[cariloha-bundle-swap] Swap failed:', error);
    deferred.reject(error);
    showError(errorEl, 'Network error. Please try again.');
    button.removeAttribute('aria-busy');
  }
}

/**
 * @param {Element | null | undefined} errorEl
 * @param {string} message
 */
function showError(errorEl, message) {
  if (!(errorEl instanceof HTMLElement)) return;
  errorEl.textContent = message;
  errorEl.hidden = false;
}
